import { useGcsStore } from '../../../store/useGcsStore';
import { useSelectedVehicleStore } from '../../../store/useSelectedVehicleStore';
import { selectIsVehicleUnderMyControl } from '../../../store/selectors/selectIsVehicleUnderMyControl';
import { type MissionId } from '../../../types/gcs/mission';
import { VehicleMarker } from './markers/VehicleMarker';
import { selectIsVehicleConnected } from '../../../store/selectors/selectIsVehicleConnected';
import { getShortVehicleId } from '../../../utils/formatters';
import { selectVehicleStatusCategory } from '../../../store/selectors/selectVehicleStatusCategory';

interface SingleMissionVehicleMarkerProps {
  gcsId: string;
  missionId: MissionId;
}

export const SingleMissionVehicleMarker = ({ gcsId, missionId }: SingleMissionVehicleMarkerProps) => {
  const flightData = useGcsStore((state) => {
    const gcsData = state.data.get(gcsId);
    return gcsData?.missionFlightDataMap?.get(missionId);
  });

  const hasPackage = useGcsStore((state) => {
    const gcsData = state.data.get(gcsId);
    return gcsData?.missionDetailedStateMap?.get(missionId)?.hasPackage;
  });

  const vehicleId = useGcsStore((state) => {
    const gcsData = state.data.get(gcsId);
    const liveMission = Array.from(gcsData?.liveMissionsMap.values() ?? []).find(
      (mission) => mission.missionId === missionId
    );
    return liveMission?.vehicleId;
  });

  const vehicleCategory = useGcsStore((state) => {
    if (vehicleId === undefined) return null;
    return selectVehicleStatusCategory(state.data, gcsId, vehicleId);
  });

  const isVehicleConnected =
    useGcsStore((state) => vehicleId !== undefined && selectIsVehicleConnected(state.data, gcsId, vehicleId)) ?? false;

  const isVehicleUnderMyControl = useGcsStore((state) => {
    if (vehicleId === undefined) return false;
    return selectIsVehicleUnderMyControl(state.data, gcsId, vehicleId);
  });

  const selectedVehicle = useSelectedVehicleStore((state) => state.selectedVehicle);

  const isVehicleSelected = Boolean(
    vehicleId !== undefined &&
    selectedVehicle &&
    selectedVehicle.gcsId === gcsId &&
    selectedVehicle.vehicleId === vehicleId
  );

  if (vehicleId === undefined || !flightData || hasPackage === undefined) {
    return null;
  }

  const vehicleLabel = getShortVehicleId(vehicleId);

  return (
    <>
      {flightData?.location && (
        <VehicleMarker
          gcsId={gcsId}
          missionId={flightData.missionId}
          vehicleLabel={vehicleLabel}
          longitude={flightData.location.lng}
          latitude={flightData.location.lat}
          altitude={flightData.location.alt}
          rotation={flightData.attitude.yaw}
          vehicleCategory={vehicleCategory}
          hasPackage={hasPackage}
          isUnderMyControl={isVehicleUnderMyControl}
          isConnected={isVehicleConnected}
          markerSize={isVehicleSelected ? 72 : 64}
        />
      )}
    </>
  );
};
